import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { EMOTIONS } from "@/lib/emotions";
import { routeById } from "@/lib/routes";
import { LogoutButton } from "@/components/LogoutButton";
import { useAuth } from "@/context/AuthContext";
import { useRemanso } from "@/context/RemansoContext";
import { Cover } from "@/components/Cover";
import { countWords } from "@/lib/emotions";
import { newEntryPath } from "@/pages/Escribir";
import { InstallCard } from "@/components/InstallCard";

const TABS = [
  { to: "/escribir", label: "Escribir", id: "escribir" },
  { to: "/habitos", label: "Hábitos", id: "habitos" },
  { to: "/mi-mes", label: "Mi mes", id: "mi-mes" },
];

export const NavTabs = ({ prefix, onNavigate, className = "" }) => (
  <nav className={`segmented ${className}`} aria-label="Secciones">
    {TABS.map(({ to, label, id }) => (
      <NavLink key={id} to={to} onClick={onNavigate} data-testid={`${prefix}-tab-${id}`}
        className={({ isActive }) => `segmented-item ${isActive ? "active" : ""}`}>
        {label}
      </NavLink>
    ))}
  </nav>
);

const fmt = (iso) => new Date(iso).toLocaleDateString("es-MX", { day: "numeric", month: "short" }).replace(".", "");
const firstLine = (t) => (t || "").split("\n")[0].trim();

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const emotionName = (id) => EMOTIONS.find((e) => e.id === id)?.name || id;
// searches the user's own decrypted entries in memory; nothing is indexed on the server
const haystack = (e) => norm([e.title, e.body, e.route && e.route !== "libre" ? routeById(e.route).name : "",
  ...(e.moods || []).flatMap((m) => [emotionName(m.split(":")[0]), m.split(":").pop()])].join(" "));

export const EntryList = ({ entries, error, prefix, onNavigate }) => {
  const [q, setQ] = useState("");
  const terms = norm(q).split(/\s+/).filter(Boolean);
  const shown = terms.length ? entries.filter((e) => { const h = haystack(e); return terms.every((t) => h.includes(t)); }) : entries;
  return (
  <div className="flex-1 min-h-0 flex flex-col" data-testid={`${prefix}-entry-list`}>
    {entries.length > 0 && (
      <label className="relative block mb-2">
        <span className="sr-only">Buscar entradas</span>
        <Search size={16} strokeWidth={1.75} className="absolute left-3 top-1/2 -translate-y-1/2 text-meta" aria-hidden="true" />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar entradas" title="Busca por palabra, ruta o emoción"
          className="field pl-9 py-2 text-[14px]" data-testid={`${prefix}-entry-search`} />
      </label>
    )}
    {terms.length > 0 && shown.length === 0 && (
      <p className="text-sm text-sec px-1" data-testid={`${prefix}-entry-search-empty`}>Ninguna entrada coincide con “{q.trim()}”.</p>
    )}
    {error && <p className="calm-note" data-testid={`${prefix}-entry-list-error`}>{error}</p>}
    {!error && entries.length === 0 && (
      <p className="text-sm text-sec leading-relaxed px-1" data-testid={`${prefix}-entry-list-empty`}>
        Todavía no escribes hoy.
      </p>
    )}
    <ul className="overflow-y-auto space-y-1">
      {shown.map((e) => (
        <li key={e.id}>
          <NavLink to={`/escribir/${e.id}`} onClick={onNavigate} data-testid={`${prefix}-entry-${e.id}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-[8px] border px-2.5 py-2.5 transition-colors ${isActive ? "bg-hoja border-linea" : "border-transparent hover:bg-superficie"}`}>
            <Cover id={e.id} words={countWords(e.body)} moods={e.moods} width={32} height={32} className="shrink-0 rounded-[6px]" />
            <span className="min-w-0">
              <span className="block text-xs text-meta">{fmt(e.created_at)}</span>
              <span className="block font-serif text-[15px] text-tinta truncate">{e.title.trim() || firstLine(e.body) || "Entrada sin texto"}</span>
            </span>
          </NavLink>
        </li>
      ))}
    </ul>
  </div>
  );
};

export const SideContent = ({ entries, error, prefix, onNavigate, showTabs = true }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openInfo } = useRemanso();
  const open = (view) => { onNavigate?.(); openInfo(view); };
  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      <button className="btn-primary w-full" data-testid={`${prefix}-new-entry-button`}
        onClick={() => { navigate(...newEntryPath()); onNavigate?.(); }}>
        Nueva entrada
      </button>
      {showTabs && <NavTabs prefix={prefix} onNavigate={onNavigate} />}
      <InstallCard entries={entries} prefix={prefix} onNavigate={onNavigate} />
      <EntryList entries={entries} error={error} prefix={prefix} onNavigate={onNavigate} />
      <div className="flex flex-col items-start gap-1">
        <button type="button" className="btn-text text-sm -ml-2" onClick={() => open("guia")} data-testid={`${prefix}-guide-link`}>Guía de emociones</button>
        <button type="button" className="btn-text text-sm -ml-2" onClick={() => open("historia")} data-testid={`${prefix}-story-link`}>Los Remansos</button>
        <NavLink to="/mi-cuenta" onClick={onNavigate} className="btn-text text-sm -ml-2" data-testid={`${prefix}-account-link`}>Mi cuenta</NavLink>
        <NavLink to="/privacidad" onClick={onNavigate} className="btn-text text-sm -ml-2" data-testid={`${prefix}-privacy-link`}>Privacidad</NavLink>
        <NavLink to="/terminos" onClick={onNavigate} className="btn-text text-sm -ml-2" data-testid={`${prefix}-terms-link`}>Términos</NavLink>
      </div>
      <div className="pt-4 border-t border-linea flex items-center justify-between gap-2">
        <span className="text-xs text-meta truncate" data-testid={`${prefix}-user-name`}>{user?.name || user?.email}</span>
        <LogoutButton className="btn-text text-xs" testId={`${prefix}-logout-button`} icon />
      </div>
    </div>
  );
};
